import { useEffect, useRef } from 'react';
import { IconFileTypePdf } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { fileKeyOf } from './usePdfPageEdits';
import PdfPageEditGrid from './PdfPageEditGrid';

/**
 * Per-file page editor. Active file is chosen from the left sidebar list
 * (no right-side file tabs).
 */
export default function PdfPageEditor({
    files,
    pagesByKey,
    onPagesChangeForKey,
    activeIndex = 0,
    onActiveIndexChange,
    selectable = false,
    isSelected,
    onToggleSelect,
}) {
    const { t } = useTranslation();
    const thumbCacheRef = useRef(new Map());

    const safeIndex = files.length > 0 ? Math.min(activeIndex, files.length - 1) : 0;
    const activeFile = files[safeIndex] ?? null;
    const activeKey = activeFile ? fileKeyOf(activeFile) : null;
    const activePages = activeKey ? pagesByKey[activeKey] : null;

    useEffect(() => {
        if (files.length > 0 && activeIndex !== safeIndex) {
            onActiveIndexChange?.(safeIndex);
        }
    }, [files.length, activeIndex, safeIndex, onActiveIndexChange]);

    if (!activeFile) return null;

    return (
        <div className="flex h-full min-h-0 w-full flex-col">
            <div className="min-h-0 flex-1">
                {Array.isArray(activePages) && activePages.length ? (
                    <PdfPageEditGrid
                        key={activeKey}
                        file={activeFile}
                        pages={activePages}
                        onPagesChange={(updater) => onPagesChangeForKey(activeKey, updater)}
                        thumbCache={thumbCacheRef.current}
                        selectable={selectable}
                        isSelected={isSelected}
                        onToggleSelect={onToggleSelect}
                    />
                ) : activePages === 'oversize' ? (
                    <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 px-6 text-center text-[13px] text-[#A8A099] dark:text-zinc-500">
                        <IconFileTypePdf size={30} stroke={1.5} className="text-pdf-cat-solid" />
                        {t('pdfPageEditor.tooManyPages')}
                    </div>
                ) : (
                    <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-3 text-[13px] text-[#A8A099] dark:text-zinc-500">
                        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-100 border-t-primary dark:border-primary-900 dark:border-t-primary" />
                        {t('pdfPageEditor.readingPages')}
                    </div>
                )}
            </div>
        </div>
    );
}
